import React, { useEffect } from 'react';
import { VStack, Box, HStack, Skeleton } from 'native-base';
import Card from '.';
import Barcode from '../Barcode';
import {
    GIFTCARD_FLOWS,
    GiftCard,
    observer,
    useUpdateGiftCard,
    useLocationListQuery,
    useAppSettings,
    useI18n,
    GiftCardLocationResponse,
    useSpringBigStore
} from '@springbig/shared';
import { Caption, CaptionError } from '~/shared/Typography';
import { useToast } from '~/hooks/toast';
import Button from '~/shared/Button';
import { useAppNavigate } from '~/pages';
import Pressable from '~/shared/Pressable';
import { isAfter, addDays } from 'date-fns';

type CradHolder = {
    captionColor: string;
    cardContent: GiftCard;
    openNameModal: (value: boolean) => void;
    setOnSubmitAction: (value: GIFTCARD_FLOWS | undefined) => void
}

const CardHolder = ({ captionColor, cardContent, openNameModal, setOnSubmitAction }: CradHolder) => {

    const { mutate: updateGiftCard, isLoading, error: requestGiftCardUpdateError } = useUpdateGiftCard();
    const [submitError, setSubmitError] = React.useState('');
    const { successToast } = useToast();
    const { data: locations } = useLocationListQuery();
    const { t } = useI18n();
    const { merchantOrg } = useAppSettings();
    const { navigate } = useAppNavigate();
    const { walletId, lastNameCheckDate, setLastNameCheckDate } = useSpringBigStore();
    const { member } = useSpringBigStore();

    let purchaseLocation: GiftCardLocationResponse[] | string | null = (locations && locations.length > 0 && locations?.filter(location => location.id.toString() === cardContent.purchase_location)) || null;
    purchaseLocation = purchaseLocation && purchaseLocation.length > 0 ? purchaseLocation[0].display_name : merchantOrg;

    const onManageFundsPress = () => {

        if (!member?.first_name || !member?.last_name || !member.confirmed_identity) {
            openNameModal(true);
            setOnSubmitAction(GIFTCARD_FLOWS.addFunds);
        }
        else {
            observer.emit('on-manage-cash', cardContent);
            observer.emit('open-actionsheet', GIFTCARD_FLOWS.addFunds);
        }
    };

    useEffect(() => {

        requestGiftCardUpdateError && setSubmitError(t('giftCard.update.error'));
    }, [requestGiftCardUpdateError]);

    const onMakePrimary = () => {

        updateGiftCard({
            id: cardContent.id,
            member_id: cardContent.member_id,
            auto_reload: cardContent.auto_reload,
            primary_card: true
        }, {
            onSuccess: (_) => {

                observer.emit('on-set-primary-gift-card');
                successToast(`${t('giftCard.update.success')} ${cardContent.card_number.slice(-5)}`);
            }
        });
    };

    const showTransactions = () => {

        if (!member?.first_name || !member?.last_name || !member.confirmed_identity) {
            if (!lastNameCheckDate || isAfter(
                new Date(),
                addDays(new Date(lastNameCheckDate), 4)
            )) {
                openNameModal(true);
                setLastNameCheckDate(new Date().toString());
                return;
            }
        }

        navigate(`w/${walletId}/giftCard/${cardContent.id}?page=1`, {
            state: {
                id: cardContent.id,
                cardNumber: cardContent.card_number
            },
        });

    };

    const CardRenderer = () => {

        return (
            <Box w='90%' alignSelf='center'>
                <Card card={cardContent} locations={locations} />
                <HStack justifyContent={'space-between'} alignItems={'baseline'}>
                    { isLoading ?
                        <Skeleton h={5} w={'6.3rem'} rounded={'lg'} mt={1} />
                        :
                        cardContent.primary_card ?
                            <Caption
                                fontSize={16}
                                mt={1}
                                fontWeight={'300'}
                                text='giftCard.primary.title'
                            />
                            :
                            <Button
                                isDisabled={isLoading || cardContent.primary_card || cardContent.is_closed_loop}
                                display='flex'
                                justifyContent='flex-start'
                                m={0}
                                p={0}
                                onPress={onMakePrimary}
                            >
                                <Caption
                                    fontSize={16}
                                    mt={1}
                                    fontWeight={'300'}
                                    text='giftCard.primary.button'
                                />
                            </Button>
                    }
                    <Pressable
                        display='flex'
                        justifyContent='flex-start'
                        onPress={showTransactions}
                    >
                        <Caption
                            alignItems={'flex-start'}
                            fontSize={18}
                            mt={1}
                            text='giftCard.transactions.view_transaction'
                        />
                    </Pressable>
                </HStack>
                {submitError &&
                    <CaptionError>{ submitError }</CaptionError>
                }
            </Box>
        );
    };



    return (
        <VStack >
            <CardRenderer />
            <BarcodeRender
                cardContent={cardContent}
                onManageFundsPress={onManageFundsPress}
            />
        </VStack>
    );
};

const BarcodeRender = ({
    cardContent,
    onManageFundsPress,
}: {
    cardContent: GiftCard,
    onManageFundsPress: () => void,
}) => {

    const { merchant, giftCards } = useSpringBigStore();
    const { t } = useI18n();

    return (
        <Box>
            <Barcode
                cardNumber={cardContent.card_number}
                cardPin={cardContent.card_pin}
            />
            <HStack justifyContent={'space-around'} alignItems={'baseline'} px={4} minH={4}>
                {
                    merchant &&
                    !merchant.hide_gift_card_payments &&
                    !merchant.gift_cards_purchase_disabled &&
                    ((cardContent.is_closed_loop && giftCards.length < 2 && cardContent.balance === '$0.00') || !cardContent.is_closed_loop) &&
                    <Button onPress={onManageFundsPress} mt={2}>
                        <Caption
                            fontSize={24}
                            fontWeight={'200'}
                        >
                            { t('giftCard.manage_funds') }
                        </Caption>
                    </Button>
                }
            </HStack>
        </Box>
    );
};

export default CardHolder;
